import { type Database } from "@fitness-app/supabase";

export type ProductClientToImportEntity = Database["public"]["Tables"]["product_client_import"]["Row"];
export type ProductClientsImportAggregationEntity = Database["public"]["Tables"]["product_import_aggregation"]["Row"];

export enum ClientImportStatus {
  Pending = "pending",
  Imported = "imported",
  Failed = "failed",
  /** Aktywna klientka pominięta polityką duplikatów `skip` — nie porażka, nie sukces (TRE-2110). */
  SkippedDuplicate = "skipped_duplicate",
  /** Aktywna klientka, której koniec dostępu przesunięto polityką `extend`. */
  Extended = "extended",
  /** Aktywna klientka, której źródło dostępu powiązanego podmieniono polityką `relink`. */
  Relinked = "relinked",
}

/**
 * Co zrobić z wierszem, gdy klientka jest już aktywna w produkcie (TRE-2110).
 * `skip` = dzisiejsze zachowanie (bez zmian w dostępie), `extend` = przesuń koniec
 * dostępu, jeśli nowy jest późniejszy, `relink` = podmień źródło dostępu powiązanego.
 */
export enum ImportDuplicatePolicy {
  Skip = "skip",
  Extend = "extend",
  Relink = "relink",
}

/** Skąd wystartował import — lista importów w produkcie rozróżnia je po tym. */
export enum ClientImportSource {
  Admin = "admin",
  Automation = "automation",
  CustomerGrant = "customer_grant",
  /** admin-service `POST /add-to-product/:productId` — external integrations with the import API key. */
  Api = "api",
}

export interface ProductClientToImportDocument extends ProductClientToImportEntity {
  status: ClientImportStatus;
  duplicatePolicy: ImportDuplicatePolicy;
}

/**
 * Powody, z których wiersz nie kończy się jako `imported`. Trzymane w `errorMessage`;
 * admin tłumaczy je na copy, wszystko inne pokazuje surowo.
 */
export const CLIENT_IMPORT_REASON = {
  clientAlreadyExists: "client-already-exists",
  noSourceAccess: "no-source-access",
  sourceProductNotFound: "source-product-not-found",
  wrongStatus: "wrong-status",
} as const;

/**
 * Źródło okresu dostępu wiersza importu. Dotąd jedynym źródłem była cena
 * (`priceId`); dodatek do pakietu potrzebuje dostępu, który podąża za dostępem
 * w INNYM produkcie (`linked`), a ręczna data bez ceny była dotąd możliwa tylko
 * jako nadpisanie daty przy cenie. Kolumny w tabeli są płaskie — ten typ jest
 * widokiem, który buduje `resolveImportAccessSource`.
 */
export type ImportAccessSource =
  | { kind: "price"; priceId: string; accessTo: string | null }
  | { kind: "linked"; sourceProductId: string; followSource: boolean }
  | { kind: "manual"; accessTo: string | null };

export const resolveImportAccessSource = (
  doc: Pick<ProductClientToImportDocument, "priceId" | "sourceProductId" | "followSource" | "accessTo">,
): ImportAccessSource => {
  if (doc.sourceProductId) {
    return { kind: "linked", sourceProductId: doc.sourceProductId, followSource: doc.followSource };
  }

  if (doc.priceId) {
    return { kind: "price", priceId: doc.priceId, accessTo: doc.accessTo ?? null };
  }

  return { kind: "manual", accessTo: doc.accessTo ?? null };
};

export enum ClientImportAggregation {
  InProgress = "inProgress",
  Finished = "finished",
}

export interface ProductClientsImportAggregation extends ProductClientsImportAggregationEntity {
  status: ClientImportAggregation;
  source: ClientImportSource;
}

/** `GET /products/:productId/imports/:importId` — one import with its rows (TRE-2110). */
export interface ProductImportSummary {
  aggregation: ProductClientsImportAggregation;
  clients: ProductClientToImportDocument[];
}

/**
 * `POST /products/:productId/imports/preview` — what the stepper shows before
 * anything is queued (TRE-2110): who is already active in the product and,
 * for a linked access source, whether each e-mail has an access to follow.
 */
export interface ImportPreviewRequest {
  emails: string[];
  sourceProductId?: string | null;
}

export interface ImportPreviewRow {
  email: string;
  /** Status of the existing row in the target product, `null` when there is none. */
  existingStatus: "active" | "archived" | null;
  /** Only when `sourceProductId` was given: the access the row would follow. */
  sourceAccess: null | { exists: boolean; endsAt: number | null };
}

export interface ImportPreviewResponse {
  rows: ImportPreviewRow[];
}

/**
 * `POST /products/:productId/clients/grant` — one client added from the
 * customer profile (TRE-2110). Goes through the import pipeline as a batch of
 * one, so the outcome is a regular import summary.
 */
export interface GrantProductAccessRequest {
  email: string;
  firstName: string;
  lastName?: string | null;
  phoneNumber?: string | null;
  accessSource: ImportAccessSource;
  duplicatePolicy?: ImportDuplicatePolicy;
  sendEmailAboutAccountCreation?: boolean;
  /** ISO date; defaults to now. */
  subscribedDate?: string | null;
}

export interface GrantProductAccessResponse {
  importId: string;
}

/** `POST /products/:productId/imports/:importId/retry` — the new import that re-queues the failed rows. */
export interface RetryProductImportResponse {
  importId: string;
  retried: number;
}
